import React, { ReactNode, useState } from 'react';
import {
  ProductFilterFieldDef,
  ProductFilterMark,
  ProductFilterOption,
  ProductFilterValue,
  productFilterValueLabel,
} from '../../utils/productBrowserFilterState';
import { FilterPill as UiFilterPill } from '../ui/FilterPill';
import FilterValueMenu from './FilterValueMenu';
import { BrandMark } from './BrandMark';

export interface FilterPillProps {
  field: ProductFilterFieldDef;
  options: ProductFilterOption[];
  value: ProductFilterValue;
  /** True once the value differs from the field's quiet default — drives dim vs. blue. */
  active: boolean;
  /** A single resolved brand/vendor mark to show when exactly one value is selected. */
  leadingMark?: ProductFilterMark;
  optionLabel?: (value: string) => string;
  hint?: string | null;
  /** Rendered under the value menu's options. */
  menuFooter?: ReactNode;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onChange: (value: ProductFilterValue) => void;
  onRemove?: () => void;
  /** Pinned pills stay in the bar; this resets the value (e.g. Brand → All brands). */
  onClear?: () => void;
}

/**
 * One product-browser filter pill: Brand and Status are pinned (always shown).
 * When Brand has a selection, × clears it back to All brands without removing
 * the pill. Category and Store sync are added on demand and can be removed
 * entirely. The pill itself is the shared `components/ui/FilterPill`; this
 * wrapper only knows the product field types and value menu.
 */
export default function FilterPill({
  field,
  options,
  value,
  active,
  leadingMark,
  optionLabel,
  hint,
  menuFooter,
  open,
  onOpen,
  onClose,
  onChange,
  onRemove,
  onClear,
}: FilterPillProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const valueLabel = productFilterValueLabel(field, value, optionLabel);
  const removable = !field.pinned && !field.locked && !!onRemove;
  const clearable = !field.locked && active && !!onClear;
  const trailing = clearable
    ? { onClick: onClear as () => void, title: `Clear ${field.label}`, ariaLabel: `Clear ${field.label} filter` }
    : removable
      ? { onClick: onRemove as () => void, title: `Remove ${field.label} filter`, ariaLabel: `Remove ${field.label} filter` }
      : null;

  return (
    <>
      <UiFilterPill
        label={field.label}
        valueLabel={valueLabel}
        active={active}
        locked={!!field.locked}
        open={open}
        onOpen={onOpen}
        leading={leadingMark ? <BrandMark mark={leadingMark} size={20} fontSize={10} /> : undefined}
        trailing={trailing}
        anchorRef={setAnchorEl}
      />
      {!field.locked && (
        <FilterValueMenu
          open={open}
          anchorEl={anchorEl}
          onClose={onClose}
          field={field}
          options={options}
          value={value}
          onChange={onChange}
          hint={hint}
          footer={menuFooter}
        />
      )}
    </>
  );
}
